/**
 * Phase 8 T-06c §21.C / §26.B — Sidebar recent-search chip (pure helpers).
 *
 * Stores the most-recent Smart Match thread reference in
 * `localStorage.ggj.smartmatch.recent`. Max one entry. Expires 7 days after
 * the last `lastOpenedAt` touch — the sidebar reads through `readRecent()`
 * which returns `null` once the TTL elapses.
 *
 * All Date operations accept an explicit `now` argument so tests can mock
 * `Date.now()` deterministically.
 */

export interface RecentSearchChip {
  id: string;
  name: string;
  createdAt: number;
  lastOpenedAt: number;
}

export const RECENT_CHIP_LS_KEY = 'ggj.smartmatch.recent';
export const RECENT_CHIP_TTL_MS = 7 * 24 * 60 * 60 * 1000;
export const RECENT_CHIP_CHANGE_EVENT = 'ggj:smartmatch-recent-changed';

function safeStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function isValidChip(v: unknown): v is RecentSearchChip {
  if (!v || typeof v !== 'object') return false;
  const r = v as Record<string, unknown>;
  return (
    typeof r.id === 'string' &&
    r.id.length > 0 &&
    typeof r.name === 'string' &&
    r.name.length > 0 &&
    typeof r.createdAt === 'number' &&
    typeof r.lastOpenedAt === 'number'
  );
}

/**
 * Returns `true` when `chip` should be considered expired. Exposed for
 * testability — the check is a pure inequality against `lastOpenedAt`.
 */
export function isRecentChipExpired(chip: RecentSearchChip, now: number): boolean {
  return now - chip.lastOpenedAt > RECENT_CHIP_TTL_MS;
}

export function parseRecentChip(raw: string | null, now: number): RecentSearchChip | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isValidChip(parsed)) return null;
    if (isRecentChipExpired(parsed, now)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function readRecent(now: number = Date.now()): RecentSearchChip | null {
  const s = safeStorage();
  if (!s) return null;
  const raw = s.getItem(RECENT_CHIP_LS_KEY);
  const chip = parseRecentChip(raw, now);
  if (raw && !chip) {
    // Expired or invalid — drop it so we don't re-read on every tick.
    try {
      s.removeItem(RECENT_CHIP_LS_KEY);
    } catch {
      /* ignore */
    }
  }
  return chip;
}

export function writeRecent(chip: RecentSearchChip): void {
  const s = safeStorage();
  if (!s) return;
  try {
    s.setItem(RECENT_CHIP_LS_KEY, JSON.stringify(chip));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(RECENT_CHIP_CHANGE_EVENT));
    }
  } catch {
    /* quota / private mode */
  }
}

export function clearRecent(): void {
  const s = safeStorage();
  if (!s) return;
  try {
    s.removeItem(RECENT_CHIP_LS_KEY);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(RECENT_CHIP_CHANGE_EVENT));
    }
  } catch {
    /* ignore */
  }
}

export function touchRecent(
  id: string,
  name: string,
  now: number = Date.now(),
): void {
  const existing = readRecent(now);
  const chip: RecentSearchChip = {
    id,
    name,
    createdAt: existing && existing.id === id ? existing.createdAt : now,
    lastOpenedAt: now,
  };
  writeRecent(chip);
}

/**
 * Short "2 days", "5h", "just now" relative label. Kept here (not imported
 * from date-fns) so the helper stays zero-dep and trivially testable.
 */
export function relativeTimeLabel(from: number, now: number = Date.now()): string {
  const delta = Math.max(0, now - from);
  const mins = Math.round(delta / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days === 1) return '1 day';
  if (days < 30) return `${days} days`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo`;
  const years = Math.round(months / 12);
  return `${years}y`;
}
